import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import {
  ErrorText,
  FormColumn,
  FormContainer,
  FormGroup,
  FormInput,
  FormLabel,
  FormRow,
  LoadingText,
} from './gatewayFieldStyles';

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load script: ' + src));
    document.head.appendChild(s);
  });
}

const AuthorizeNetAcceptForm = forwardRef((props, ref) => {
  const { gatewayConfig } = props || {};
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cardholderName, setCardholderName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [billingZip, setBillingZip] = useState('');

  useEffect(() => {
    let mounted = true;
    async function init() {
      setLoading(true);
      setError(null);
      try {
        // Defensive: ensure we have client side keys before loading Accept.js
        const clientKey =
          gatewayConfig?.clientKey ||
          gatewayConfig?.authorize_net_client_key ||
          gatewayConfig?.authorize_net?.clientKey ||
          process.env.REACT_APP_AUTHORIZE_NET_CLIENT_KEY ||
          process.env.REACT_APP_ANET_CLIENT_KEY ||
          null;
        const apiLoginID =
          gatewayConfig?.apiLoginID ||
          gatewayConfig?.authorize_net_api_login ||
          gatewayConfig?.authorize_net?.apiLoginID ||
          process.env.REACT_APP_AUTHORIZE_NET_API_LOGIN_ID ||
          process.env.REACT_APP_ANET_API_LOGIN_ID ||
          null;

        if (!clientKey || !apiLoginID) {
          if (mounted) {
            setError('Authorize.Net configuration missing clientKey or apiLoginID');
            setLoading(false);
          }
          return;
        }
        // Determine environment: prefer backend config, then env var, default to sandbox for safety
        const environment =
          gatewayConfig?.authorize_net_environment ||
          process.env.REACT_APP_AUTHORIZE_NET_ENV ||
          'sandbox';

        // Always use sandbox script unless explicitly set to production
        // This matches the HTML test behavior and ensures safe defaults
        const useSandboxScript = environment !== 'production';

        const scriptUrl =
          gatewayConfig?.authorize_net_script_url ||
          process.env.REACT_APP_AUTHORIZE_NET_SCRIPT_URL ||
          process.env.REACT_APP_ANET_SCRIPT_URL ||
          (useSandboxScript ? 'https://jstest.authorize.net/v1/Accept.js' : 'https://js.authorize.net/v1/Accept.js');

        await loadScript(scriptUrl);
      } catch (err) {
        console.error('Failed to load Authorize.Net Accept script', err);
        if (mounted) setError(err.message || String(err));
      } finally {
        if (mounted) setLoading(false);
      }
    }
    init();
    return () => (mounted = false);
  }, [gatewayConfig]);

  // Helper to parse expiry in various formats (MMYY, MM/YY, MM-YY)
  const parseExpiry = (value) => {
    // Remove all non-digit characters to get raw digits
    const digits = value.replace(/\D/g, '');

    if (digits.length >= 4) {
      return {
        month: digits.slice(0, 2),
        year: digits.slice(2, 4),
      };
    } else if (digits.length >= 2) {
      return {
        month: digits.slice(0, 2),
        year: digits.slice(2) || '',
      };
    }
    return { month: '', year: '' };
  };

  // Format card number with spaces for display
  const formatCardNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    const groups = digits.match(/.{1,4}/g) || [];
    return groups.join(' ').slice(0, 19); // Max 16 digits + 3 spaces
  };

  // Format expiry with slash for display
  const formatExpiry = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length >= 2) {
      return digits.slice(0, 2) + (digits.length > 2 ? '/' + digits.slice(2, 4) : '');
    }
    return digits;
  };

  // Handle card number input
  const handleCardNumberChange = (e) => {
    setCardNumber(formatCardNumber(e.target.value));
  };

  // Handle expiry input
  const handleExpiryChange = (e) => {
    setExpiry(formatExpiry(e.target.value));
  };

  // Handle CVC input (digits only)
  const handleCvcChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 4);
    setCvc(digits);
  };

  useImperativeHandle(ref, () => ({
    async getPaymentData() {
      setError(null);

      if (!cardholderName.trim()) {
        throw new Error('Cardholder name is required');
      }

      const normalizedZip = billingZip.trim().replace(/\D/g, '');
      if (!normalizedZip) {
        throw new Error('Billing ZIP code is required');
      }
      if (!/^\d{5}(\d{4})?$/.test(normalizedZip)) {
        throw new Error('Please enter a valid ZIP code');
      }

      // Parse expiry flexibly - accept MMYY, MM/YY, MM-YY formats
      const parsedExpiry = parseExpiry(expiry);
      const cleanCardNumber = cardNumber.replace(/\D/g, '');
      const cleanCvc = cvc.replace(/\D/g, '');

      // Validate that fields are not empty
      if (!cleanCardNumber) {
        throw new Error('Card number is required');
      }
      if (!parsedExpiry.month || !parsedExpiry.year) {
        throw new Error('Expiration date is required');
      }
      const expirationMonth = Number(parsedExpiry.month);
      if (!Number.isInteger(expirationMonth) || expirationMonth < 1 || expirationMonth > 12) {
        throw new Error('Expiration month must be between 01 and 12');
      }
      const now = new Date();
      const currentMonth = now.getMonth() + 1;
      const currentYear = now.getFullYear();
      const expirationYearText = String(parsedExpiry.year || '').trim();
      const expirationYear = Number(expirationYearText);
      const currentCentury = Math.floor(currentYear / 100) * 100;
      const fullExpirationYear = expirationYearText.length === 2
        ? currentCentury + expirationYear
        : expirationYear;
      if (
        !Number.isInteger(fullExpirationYear)
        || fullExpirationYear < currentYear
        || (fullExpirationYear === currentYear && expirationMonth < currentMonth)
      ) {
        throw new Error('Expiration date has expired');
      }
      if (!cleanCvc) {
        throw new Error('CVC is required');
      }

      // Determine environment: prefer backend config, then env var, default to sandbox for safety
      const environment =
        gatewayConfig?.authorize_net_environment ||
        process.env.REACT_APP_AUTHORIZE_NET_ENV ||
        'sandbox';

      const isSandbox = environment !== 'production';

      // ===== SANDBOX MODE: Bypass Accept.js, send raw card data to backend =====
      // This allows local development without HTTPS requirement
      // Backend will handle tokenization server-side
      if (isSandbox) {
        console.log('Authorize.Net Sandbox Mode: Bypassing Accept.js, sending card data to backend');

        // Return raw card data for backend to tokenize server-side
        // The backend will create customer profile using raw card data
        return {
          // Raw card data for server-side tokenization
          card_number: cleanCardNumber,
          exp_month: parsedExpiry.month,
          exp_year: parsedExpiry.year,
          cvv: cleanCvc,
          cardholder_name: cardholderName.trim(),
          postal_code: normalizedZip,
          // Flag to indicate this is raw card data, not tokenized
          raw_card_data: true,
          sandbox_mode: true,
          normalized: {
            token_type: 'raw_card',
            card_number: cleanCardNumber,
            exp_month: parsedExpiry.month,
            exp_year: parsedExpiry.year,
            cvv: cleanCvc,
            postal_code: normalizedZip,
          },
        };
      }

      // ===== PRODUCTION MODE: Use Accept.js for secure tokenization =====
      if (!window.Accept) {
        throw new Error('Authorize.Net Accept.js not loaded');
      }

      const authData = {
        clientKey:
          gatewayConfig?.clientKey ||
          gatewayConfig?.authorize_net_client_key ||
          gatewayConfig?.authorize_net?.clientKey ||
          process.env.REACT_APP_AUTHORIZE_NET_CLIENT_KEY ||
          process.env.REACT_APP_ANET_CLIENT_KEY ||
          null,
        apiLoginID:
          gatewayConfig?.apiLoginID ||
          gatewayConfig?.authorize_net_api_login ||
          gatewayConfig?.authorize_net?.apiLoginID ||
          process.env.REACT_APP_AUTHORIZE_NET_API_LOGIN_ID ||
          process.env.REACT_APP_ANET_API_LOGIN_ID ||
          null,
      };

      if (!authData.clientKey || !authData.apiLoginID) {
        throw new Error('Authorize.Net clientKey/apiLoginID missing from payment config');
      }

      // Build card data for Accept.js
      const cardData = {
        cardNumber: cleanCardNumber,
        month: parsedExpiry.month,
        year: parsedExpiry.year,
        cardCode: cleanCvc,
      };

      return new Promise((resolve, reject) => {
        const handleResponse = (response) => {
          try {
            if (!response) {
              return reject(new Error('No response from Accept.js'));
            }

            // Check for error response
            if (response.messages && response.messages.resultCode === 'Error') {
              const msg = response.messages.message && response.messages.message[0] && response.messages.message[0].text
                ? response.messages.message[0].text
                : 'Payment tokenization error';
              return reject(new Error(msg));
            }

            // Check for successful response with opaqueData
            if (response.opaqueData && response.opaqueData.dataDescriptor && response.opaqueData.dataValue) {
              return resolve({
                opaqueDataDescriptor: response.opaqueData.dataDescriptor,
                opaqueDataValue: response.opaqueData.dataValue,
                cardholder_name: cardholderName.trim(),
                postal_code: normalizedZip,
                normalized: {
                  token_type: 'opaque_data',
                  token: response.opaqueData.dataValue,
                  descriptor: response.opaqueData.dataDescriptor,
                },
              });
            }

            reject(new Error('Invalid response from Accept.js - missing opaqueData'));
          } catch (err) {
            reject(err);
          }
        };

        try {
          const paymentData = {
            cardData: cardData,
            authData: authData,
          };

          if (typeof window.Accept.dispatchData === 'function') {
            window.Accept.dispatchData(paymentData, handleResponse);
          } else {
            reject(new Error('Accept.dispatchData not available'));
          }
        } catch (err) {
          console.error('Error calling Accept.dispatchData:', err);
          reject(err);
        }
      });
    },
  }));

  return (
    <FormContainer>
      <FormGroup>
        <FormLabel htmlFor="cardholder-name">
          Cardholder Name
        </FormLabel>
        <FormInput
          type="text"
          id="cardholder-name"
          placeholder="John Doe"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
        />
      </FormGroup>

      <FormGroup>
        <FormLabel htmlFor="card-number-main">
          Card Number
        </FormLabel>
        <FormInput
          aria-label="anet-card-number"
          placeholder="4242 4242 4242 4242"
          value={cardNumber}
          onChange={handleCardNumberChange}
          maxLength={19}
          inputMode="numeric"
        />
      </FormGroup>

      <FormRow>
        <FormColumn>
          <FormLabel htmlFor="expiration-date">
            Expiration Date
          </FormLabel>
          <FormInput
            aria-label="anet-expiry"
            placeholder="MM/YY"
            value={expiry}
            onChange={handleExpiryChange}
            maxLength={5}
            inputMode="numeric"
          />
        </FormColumn>

        <FormColumn>
          <FormLabel htmlFor="cvc">
            CVC
          </FormLabel>
          <FormInput
            aria-label="anet-cvc"
            placeholder="123"
            value={cvc}
            onChange={handleCvcChange}
            maxLength={4}
            inputMode="numeric"
          />
        </FormColumn>
      </FormRow>

      <FormGroup>
        <FormLabel htmlFor="billing-zip">Billing ZIP Code</FormLabel>
        <FormInput
          aria-label="anet-billing-zip"
          placeholder="12345"
          value={billingZip}
          onChange={(e) => setBillingZip(e.target.value)}
          inputMode="numeric"
        />
      </FormGroup>

      {loading && <LoadingText>Loading payment form...</LoadingText>}
      {error && <ErrorText>{error}</ErrorText>}
    </FormContainer>
  );
});

export default AuthorizeNetAcceptForm;
